import type { MaybeRefOrGetter } from "vue";

import { isString } from "radash";
import { toValue } from "vue";

import type { LocaleMorpheus } from "@solvari/translations";

import { localeToIso, useI18nCommonFe } from "@solvari/translations";
import { arrayWrap, asyncThrottle, isError } from "@solvari/utils";

import type { PrimitiveOrArrayValue } from "@/lib/components/logic/atoms/input/props";
import type { PhoneCountry } from "@/lib/validation/rules/phone.api.ts";

import { eager } from "@/lib/validation/events";
import { defineRule } from "@/lib/validation/rules/defineRule";
import { getPhoneValidation } from "@/lib/validation/rules/phone.api.ts";

function countryName(country: PhoneCountry, locale: LocaleMorpheus) {
  return (
    new Intl.DisplayNames([localeToIso(locale)], { type: "region" }).of(
      country,
    ) ?? country
  );
}

const required = defineRule(
  (value: unknown) => {
    if (Array.isArray(value)) {
      return !!value.length;
    }
    if (typeof value === "number") {
      return true;
    }
    return !!value;
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "required",
      message: ({ messageParams }) =>
        tr("common_fe.validation.required", messageParams),
    };
  },
);

const minLength = defineRule(
  (
    value: PrimitiveOrArrayValue | null | undefined,
    params: number | string,
  ) => {
    if (!value) {
      return params === 0;
    }
    if (Array.isArray(value)) {
      return value.every(
        (singleValue: boolean | number | string) =>
          singleValue.toString().length >= Number(params),
      );
    }
    return value.toString().length >= Number(params);
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "minLength",
      message: ({ messageParams }) =>
        tr("common_fe.validation.min_length", messageParams),
    };
  },
);

const maxLength = defineRule(
  (
    value: PrimitiveOrArrayValue | null | undefined,
    params: number | string,
  ) => {
    if (!value) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.every(
        (singleValue: boolean | number | string) =>
          singleValue.toString().length <= Number(params),
      );
    }
    return value.toString().length <= Number(params);
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "maxLength",
      message: ({ messageParams }) =>
        tr("common_fe.validation.max_length", messageParams),
    };
  },
);

const telephone = defineRule(
  async (
    phone: PrimitiveOrArrayValue | null | undefined,
    countryParam: MaybeRefOrGetter<PhoneCountry | undefined>,
    throttledValidate,
  ) => {
    const country = toValue(countryParam);
    if (!phone) {
      return false;
    }

    async function validatePhone(phone: boolean | number | string) {
      if (!isString(phone) || /[^\d()+\-/\s]/.test(phone)) {
        return false;
      }

      const phoneDigits = phone.replace(/\D/g, "");
      if (phoneDigits.length < 7 || phoneDigits.length > 14) {
        return false;
      }

      const result = country
        ? await throttledValidate(
            phone.trim().startsWith("+") ? `+${phoneDigits}` : phoneDigits,
            country,
          )
        : await throttledValidate(phoneDigits);
      return isError(result) || result;
    }

    const results = await Promise.all(
      arrayWrap(phone).map((phone) => validatePhone(phone)),
    );

    return results.every(Boolean);
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });

    return {
      name: "telephone",
      events: eager,
      message: ({ messageParams, params }) => {
        const country = toValue(params);
        return country
          ? tr("common_fe.validation.telephone_country", {
              ...messageParams,
              country: countryName(country, locale.value),
            })
          : tr("common_fe.validation.telephone", messageParams);
      },
    };
  },
  () => asyncThrottle(getPhoneValidation),
);

const email = defineRule(
  (value: PrimitiveOrArrayValue | null | undefined) => {
    if (!value) {
      return false;
    }

    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return arrayWrap(value).every(
      (value) => typeof value === "string" && emailRegex.test(value),
    );
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "email",
      events: eager,
      message: ({ messageParams }) =>
        tr("common_fe.validation.email", messageParams),
    };
  },
);

const patternRule = defineRule(
  (value: unknown, regex: (RegExp | string)[] | RegExp | string) => {
    if (
      !(
        typeof value === "string" ||
        typeof value === "number" ||
        Array.isArray(value)
      ) ||
      !value
    ) {
      return false;
    }

    const mappedRegex = arrayWrap(regex).map((singleRegex: RegExp | string) => {
      if (typeof singleRegex === "string") {
        return new RegExp(singleRegex);
      }
      return singleRegex;
    });

    return arrayWrap(value).every((singleValue) => {
      return mappedRegex.some((singleMappedRegex) =>
        singleMappedRegex.test(String(singleValue)),
      );
    });
  },
  (locale) => {
    const { tr } = useI18nCommonFe({ locale });
    return {
      name: "pattern",
      message: ({ messageParams }) =>
        tr("common_fe.validation.pattern", messageParams),
    };
  },
);

export { email, maxLength, minLength, patternRule, required, telephone };
